import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Switch } from "../../../components/ui";
import { cancelVideoRelease, getVideoSchedule, scheduleVideoRelease } from "../../../services/content";
import "../payments-ledger/payments.css";

const pad = (n) => String(n).padStart(2, "0");
/** Value of a datetime-local input, `minutes` from now. */
const localValue = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
const when = (date) => new Date(date).toLocaleString([], { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

/**
 * Scheduled release of a video, on its page: shows the planned date (with
 * a cancel button) or a button that opens the scheduling window (date and
 * time, push notification sent right after the release).
 */
const ScheduleRelease = ({ video, onChange }) => {
  const { t } = useTranslation();
  const [schedule, setSchedule] = useState(null);
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState("");
  const [notify, setNotify] = useState(true);
  const [spread, setSpread] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const online = video?.status === true && video?.availability === "released";

  useEffect(() => {
    if (!video?._id || online) return;
    getVideoSchedule(video._id)
      .then(setSchedule)
      .catch(() => setSchedule(null));
  }, [video?._id, online]);

  if (!video || online) return null;

  const start = () => {
    const tomorrow = new Date(Date.now() + 24 * 3600e3);
    tomorrow.setHours(18, 0, 0, 0);
    setAt(localValue(tomorrow));
    setTitle(t("New on AFO MEDIA"));
    setBody(video.media?.title || "");
    setNotify(true);
    setSpread(Boolean(schedule?.notification?.spread?.enabled));
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await scheduleVideoRelease(video._id, {
        at: new Date(at).toISOString(),
        notify: notify ? { title: title.trim(), body: body.trim(), spread } : null,
      });
      setSchedule({ scheduledReleaseAt: result.scheduledReleaseAt, notification: result.notification ? { status: "scheduled", title, body, spread: { enabled: spread } } : null });
      toast.success(t("Release scheduled for {{date}}", { date: when(result.scheduledReleaseAt) }));
      setOpen(false);
      onChange && onChange();
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await cancelVideoRelease(video._id);
      setSchedule({ scheduledReleaseAt: null, notification: null });
      toast.success(t("Scheduled release canceled"));
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {schedule?.scheduledReleaseAt ? (
        <div className="schedule-banner" role="status">
          <i className="ri-calendar-event-line" aria-hidden="true"></i>
          <span>
            {t("Goes online on {{date}}", { date: when(schedule.scheduledReleaseAt) })}
            {schedule.notification?.status === "scheduled" ? ` · ${t("with a notification to everyone")}` : ""}
            {schedule.notification?.status === "scheduled" && schedule.notification?.spread?.enabled ? ` (${t("spread in waves")})` : ""}
          </span>
          <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={start}>
            {t("Change")}
          </button>
          <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={cancel}>
            {t("Cancel the schedule")}
          </button>
        </div>
      ) : (
        <button type="button" className="ui-btn ui-btn-secondary" onClick={start}>
          <i className="ri-calendar-event-line" aria-hidden="true"></i>
          {t("Schedule the release")}
        </button>
      )}

      {open && (
        <Modal show={true} onHide={() => setOpen(false)} centered dialogClassName="ui-modal">
          <form onSubmit={save}>
            <Modal.Header>
              <Modal.Title>{t("Schedule the release")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="refund-form">
              <p className="ui-hint">{t("Until then the video is shown as « Upcoming ». It goes online by itself at this time.")}</p>
              <label className="refund-field">
                <span>{t("Date and time")}</span>
                <input type="datetime-local" className="ui-input" value={at} min={localValue(new Date(Date.now() + 5 * 60e3))} onChange={(e) => setAt(e.target.value)} aria-label={t("Date and time")} required />
              </label>
              <Switch checked={notify} onChange={setNotify} label={t("Send a notification to everyone right after")} onLabel={t("Send a notification to everyone right after")} offLabel={t("Send a notification to everyone right after")} />
              {notify && (
                <>
                  <label className="refund-field">
                    <span>{t("Notification title")}</span>
                    <input className="ui-input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} required />
                  </label>
                  <label className="refund-field">
                    <span>{t("Notification text")}</span>
                    <input className="ui-input" value={body} maxLength={300} onChange={(e) => setBody(e.target.value)} required />
                  </label>
                  <p className="ui-hint">{t("It opens the video in the app and appears in the notification history.")}</p>
                  <Switch checked={spread} onChange={setSpread} label={t("Spread the sending (expected high traffic)")} onLabel={t("Spread the sending (expected high traffic)")} offLabel={t("Spread the sending (expected high traffic)")} />
                  <p className="ui-hint">
                    {spread
                      ? t("Sent in waves every 15 minutes to logged-in users, so the servers are not flooded. Follow or stop the waves from the notification history.")
                      : t("Off: everyone receives it at once (usual sending).")}
                  </p>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setOpen(false)}>
                {t("Cancel")}
              </button>
              <button type="submit" className="ui-btn" disabled={busy || !at}>
                <i className="ri-calendar-event-line" aria-hidden="true"></i>
                {t("Schedule")}
              </button>
            </Modal.Footer>
          </form>
        </Modal>
      )}
    </>
  );
};

export default ScheduleRelease;
